import React from 'react';
import { Document, Page, Text, View, StyleSheet, Link } from '@react-pdf/renderer';
import { SerializedContent } from '@/features/screenplay/components/editor/utils/JsonUtils';

interface ScreenplayPDFTemplateProps {
    blocks: SerializedContent[];
    brandLogoBase64?: string;
    projectTitle?: string;
    authorName?: string;
}

const styles = StyleSheet.create({
    page: {
        paddingTop: 72,
        paddingBottom: 72,
        paddingLeft: 108,
        paddingRight: 72,
        fontFamily: 'Courier',
        fontSize: 12,
        lineHeight: 1.1,
    },
    scene: {
        textTransform: 'uppercase',
        fontWeight: 'bold',
        marginTop: 24,
        marginBottom: 12,
    },
    action: {
        marginBottom: 12,
    },
    pageNumber: {
        position: 'absolute',
        top: 20,
        right: 30,
        fontSize: 10,
        color: '#64748b',
        fontFamily: 'Courier',
    },
    character: {
        textTransform: 'uppercase',
        marginLeft: 144, // Roughly 2" from left margin
        marginTop: 12,
        marginBottom: 0,
    },
    dialogue: {
        marginLeft: 72,  // Roughly 1" from left margin
        marginRight: 108, // Roughly 1.5" from right margin
        marginBottom: 12,
    },
    parans: {
        marginLeft: 108, // Roughly 1.5" from left margin
        marginRight: 144,
        marginBottom: 0,
    },
    transition: {
        textTransform: 'uppercase',
        textAlign: 'right',
        marginTop: 12,
        marginBottom: 12,
    },
    shot: {
        textTransform: 'uppercase',
        marginTop: 12,
        marginBottom: 12,
    },
    titlePage: {
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 72,
        paddingRight: 72,
        fontFamily: 'Courier',
        backgroundColor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
    },
    titlePageCenter: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
    },
    titlePageTitle: {
        fontSize: 24,
        fontWeight: 'bold',
        textTransform: 'uppercase',
        textAlign: 'center',
        letterSpacing: 2,
        marginBottom: 24,
    },
    titlePageBy: {
        fontSize: 14,
        textAlign: 'center',
        marginBottom: 8,
    },
    titlePageAuthor: {
        fontSize: 14,
        fontWeight: 'bold',
        textAlign: 'center',
    },
    titlePageFooter: {
        position: 'absolute',
        bottom: 40,
        left: 0,
        right: 0,
        textAlign: 'center',
    },
    titlePageFooterText: {
        fontSize: 9,
        color: '#94a3b8',
        textAlign: 'center',
    },
});

export const ScreenplayPDFTemplate: React.FC<ScreenplayPDFTemplateProps> = ({ blocks, brandLogoBase64, projectTitle, authorName }) => {
    // Pre-compute scene numbers so each scene block knows its 1-based index
    let sceneCounter = 0;
    const sceneNumbers: Record<string, number> = {};
    for (const block of blocks) {
        if (block.type === 'scene') {
            sceneNumbers[block.id] = ++sceneCounter;
        }
    }

    return (
        <Document>
            {/* ── Title Page ── */}
            <Page size="A4" style={styles.titlePage}>
                <View style={styles.titlePageCenter}>
                    <Text style={styles.titlePageTitle}>{projectTitle || 'Untitled'}</Text>
                    <Text style={styles.titlePageBy}>by</Text>
                    <Text style={styles.titlePageAuthor}>{authorName || 'Unknown'}</Text>
                </View>
                <View style={styles.titlePageFooter}>
                    <Text style={styles.titlePageFooterText}>
                        Made with{' '}
                        <Link src="https://storyverse.aioncw.com" style={{ color: '#667eea', textDecoration: 'none' }}>
                            Storyverse
                        </Link>
                    </Text>
                </View>
            </Page>

            {/* ── Content Pages ── */}
            <Page size="A4" style={styles.page} wrap>
                {/* Page Number - top right, fixed on every content page */}
                <Text
                    style={styles.pageNumber}
                    render={({ pageNumber }) => `${pageNumber - 1}.`}
                    fixed
                />
                {blocks.map((block) => {
                    const text = block.children?.map(c => c.text).join('') || '';

                    if (block.type === 'page-break') {
                        return <Text break key={block.id}></Text>;
                    }

                    if (!text.trim()) {
                        return null; // Skip empty blocks
                    }

                    let style: any = styles.action;
                    let wrapBlock = true;
                    let displayText = text;

                    switch (block.type) {
                        case 'scene':
                            style = styles.scene;
                            wrapBlock = false;
                            // Prefix scene number — mirrors SceneNode.findSiNo() in the DOM
                            displayText = `${sceneNumbers[block.id]}. ${text}`;
                            break;
                        case 'character':
                            style = styles.character;
                            wrapBlock = false;
                            break;
                        case 'dialogue':
                            style = styles.dialogue;
                            break;
                        case 'parans':
                            style = styles.parans;
                            wrapBlock = false;
                            break;
                        case 'transition':
                            style = styles.transition;
                            wrapBlock = false;
                            break;
                        case 'shot':
                            style = styles.shot;
                            wrapBlock = false;
                            break;
                        default:
                            style = styles.action;
                            break;
                    }

                    return (
                        <Text key={block.id} style={style} wrap={wrapBlock}>
                            {displayText}
                        </Text>
                    );
                })}
            </Page>
        </Document>
    );
};
